import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'

import { Icon } from '@wartek-id/icon-v2'
import type { OptionItem } from '@wartek-id/select'

import type { KinerjaHarianView } from 'types/kinerja-harian'

interface KinerjaHarianToolbarProps {
  activeView: KinerjaHarianView
  monthOptions: OptionItem[]
  selectedMonth: OptionItem
  selectedYear: number
  onMonthChange: (option: OptionItem) => void
  onMonthClick?: () => void
  onViewChange: (view: KinerjaHarianView) => void
}

export function KinerjaHarianToolbar({
  activeView,
  monthOptions,
  selectedMonth,
  selectedYear,
  onMonthChange,
  onMonthClick,
  onViewChange,
}: KinerjaHarianToolbarProps) {
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false)
  const monthPickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        monthPickerRef.current &&
        !monthPickerRef.current.contains(event.target as Node)
      ) {
        setIsMonthPickerOpen(false)
      }
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMonthPickerOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  const handleMonthSelect = (option: OptionItem) => {
    onMonthChange(option)
    setIsMonthPickerOpen(false)
  }

  const handleMonthClick = () => {
    setIsMonthPickerOpen((prev) => !prev)
    onMonthClick?.()
  }

  return (
    <div className="flex items-center justify-between gap-4">
      <div ref={monthPickerRef} className="relative">
        <button
          type="button"
          className={clsx(
            'flex items-center justify-between rounded-lg border border-[#CBD5E1] bg-white px-4 py-3 text-left transition',
            isMonthPickerOpen &&
              'border-[#8FB3FF] shadow-[0_0_0_3px_rgba(59,130,246,0.12)]'
          )}
          aria-expanded={isMonthPickerOpen}
          aria-haspopup="dialog"
          onClick={handleMonthClick}
        >
          <div className="min-w-0 truncate font-normal leading-none text-[#3F3F46]">
            {selectedMonth.name}
          </div>

          <Icon
            className={clsx(
              'ml-4 shrink-0 text-[28px] text-[#4B5563] transition-transform',
              isMonthPickerOpen && 'rotate-180'
            )}
          >
            expand_more
          </Icon>
        </button>

        {isMonthPickerOpen && (
          <div className="absolute left-0 top-[calc(100%+12px)] z-20 w-[400px] max-w-[calc(100vw-64px)] overflow-hidden rounded-2xl border border-[#D7DCE3] bg-white shadow-[0_8px_20px_rgba(15,23,42,0.12)]">
            <div className="flex items-center justify-center border-b border-[#D9D9D9] bg-[#F5F5F5] px-6 py-[17px]">
              <div className="text-lg font-semibold leading-none text-[#2F2F2F]">
                {selectedYear}
              </div>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-3 gap-x-9 gap-y-8">
                {monthOptions
                  .filter((option) => option.option !== 'all')
                  .map((option) => (
                    <button
                      key={option.option}
                      type="button"
                      className={clsx(
                        'text-left font-medium leading-none transition',
                        selectedMonth.option === option.option
                          ? 'text-blue-50 !font-semibold'
                          : 'text-default hover:text-blue-50'
                      )}
                      onClick={() => handleMonthSelect(option)}
                    >
                      {option.name.split(' ')[0]}
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center p-1 border rounded-lg border-gray-30">
        <button
          type="button"
          className={clsx(
            'flex items-center gap-2 rounded-md px-4 py-2 leading-none transition',
            activeView === 'list' ? 'bg-blue-70 text-white' : 'text-black'
          )}
          aria-current={activeView === 'list' ? 'page' : undefined}
          onClick={() => onViewChange('list')}
        >
          <div>
            <Icon
              className="leading-none shrink-0"
              color={activeView === 'list' ? 'inherit' : undefined}
              fontSize="sm"
            >
              format_list_bulleted
            </Icon>
          </div>
          <div className="leading-none">Daftar</div>
        </button>
        <button
          type="button"
          className={clsx(
            'flex items-center gap-2 rounded-md px-4 py-2 leading-none transition',
            activeView === 'calendar' ? 'bg-blue-70 text-white' : 'text-black'
          )}
          aria-current={activeView === 'calendar' ? 'page' : undefined}
          onClick={() => onViewChange('calendar')}
        >
          <div>
            <Icon
              className="leading-none shrink-0"
              color={activeView === 'calendar' ? 'inherit' : undefined}
              fontSize="sm"
            >
              grid_on
            </Icon>
          </div>
          <div className="leading-none">Kalender</div>
        </button>
      </div>
    </div>
  )
}
